import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ApiError, apiClient } from "@/lib/api-client";

type Price = {
  id: string;
  lookup_key: string | null;
  unit_amount: number;
  currency: string;
  recurring?: {
    interval: string;
    trial_period_days?: number;
  };
  product: {
    id: string;
    name: string;
    description?: string | null;
  };
};

type PricesResponse = {
  prices: Price[];
};

export const Route = createFileRoute("/$locale/_dashboard/pricing")({
  component: PricingPage,
});

type SessionInfo = {
  userId: string;
  plan: string;
  hasSubscription: boolean;
};

const PLAN_CONFIG = {
  starter_monthly: {
    features: [
      "1,900 credits per month",
      "Unlimited workspace members",
      "Email support",
    ],
  },
  pro_monthly: {
    features: [
      "4,000 credits per month",
      "Everything in Starter",
      "Priority support",
    ],
  },
};

function getCheckoutErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    try {
      const parsed = JSON.parse(err.message) as { error?: string };
      if (parsed?.error === "TOPUP_DEBT_BLOCKED") {
        return "Top-up credits are temporarily blocked until refund reconciliation is completed.";
      }
      if (parsed?.error) {
        return parsed.error;
      }
    } catch {
      // Fallback to raw message below.
    }

    if (err.message.includes("TOPUP_DEBT_BLOCKED")) {
      return "Top-up credits are temporarily blocked until refund reconciliation is completed.";
    }
    return err.message;
  }

  if (err instanceof Error) {
    return err.message;
  }

  return "Failed to start checkout. Please try again.";
}

function PricingPage() {
  const [subscriptionPrices, setSubscriptionPrices] = useState<Price[]>([]);
  const [topupPrices, setTopupPrices] = useState<Price[]>([]);
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { locale } = Route.useParams();

  useEffect(() => {
    async function fetchData() {
      try {
        const [subscriptionPriceData, allPriceData, sessionData] = await Promise.all([
          apiClient<PricesResponse>(
            "/api/stripe/prices?lookup_keys=starter_monthly,pro_monthly",
          ),
          apiClient<PricesResponse>("/api/stripe/prices"),
          apiClient<SessionInfo>("/api/session"),
        ]);

        setSubscriptionPrices(subscriptionPriceData.prices);
        setTopupPrices(
          allPriceData.prices
            .filter((price) => !price.recurring)
            .sort((a, b) => a.unit_amount - b.unit_amount),
        );
        setSession(sessionData);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  const starterPrice = subscriptionPrices.find(
    (price) => price.lookup_key === "starter_monthly",
  );
  const proPrice = subscriptionPrices.find(
    (price) => price.lookup_key === "pro_monthly",
  );

  if (loading) {
    return (
      <div className="py-6">
        <h1 className="text-2xl font-semibold">Pricing</h1>
        <p className="text-muted-foreground mt-2">Loading plans...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-6">
        <h1 className="text-2xl font-semibold">Pricing</h1>
        <p className="text-destructive mt-2">Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="py-6">
      <h1 className="text-2xl font-semibold">Pricing</h1>
      <p className="text-muted-foreground mt-2">
        Choose a plan and add top-up credits anytime.
      </p>

      {session?.hasSubscription ? (
        <div className="mt-8 max-w-md">
          <div className="border rounded-lg p-6">
            <h2 className="text-lg font-medium mb-2">Current Plan</h2>
            <p className="text-2xl font-semibold capitalize mb-4">{session.plan}</p>
            <p className="text-muted-foreground mb-6">
              Manage your subscription, update payment method, or cancel your plan.
            </p>
            <Link
              to="/$locale/_dashboard/billings"
              params={{ locale }}
              className="block w-full text-center bg-primary text-primary-foreground rounded-md py-2"
            >
              Manage Subscription
            </Link>
          </div>
        </div>
      ) : (
        <div className="mt-8 grid md:grid-cols-2 gap-6 max-w-3xl">
          <PricingCard
            name={starterPrice?.product?.name ?? "Starter"}
            price={starterPrice?.unit_amount ?? 1900}
            interval={starterPrice?.recurring?.interval ?? "month"}
            trialDays={starterPrice?.recurring?.trial_period_days}
            features={PLAN_CONFIG.starter_monthly.features}
            priceId={starterPrice?.id}
            available={!!starterPrice}
          />
          <PricingCard
            name={proPrice?.product?.name ?? "Pro"}
            price={proPrice?.unit_amount ?? 3900}
            interval={proPrice?.recurring?.interval ?? "month"}
            trialDays={proPrice?.recurring?.trial_period_days}
            features={PLAN_CONFIG.pro_monthly.features}
            priceId={proPrice?.id}
            available={!!proPrice}
          />
        </div>
      )}

      <div className="mt-10 max-w-5xl">
        <h2 className="text-xl font-semibold">Top-up Credits</h2>
        <p className="text-muted-foreground mt-2">
          One-time credit packs for additional usage.
        </p>

        {topupPrices.length === 0 ? (
          <p className="text-muted-foreground mt-4">No top-up packs available.</p>
        ) : (
          <div className="mt-6 grid md:grid-cols-3 gap-4">
            {topupPrices.map((price) => (
              <TopupCard key={price.id} price={price} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PricingCard({
  name,
  price,
  interval,
  trialDays,
  features,
  priceId,
  available,
}: {
  name: string;
  price: number;
  interval: string;
  trialDays?: number;
  features: string[];
  priceId?: string;
  available: boolean;
}) {
  const [submitting, setSubmitting] = useState(false);

  async function handleSubscribe() {
    if (!priceId) return;
    setSubmitting(true);

    try {
      const data = await apiClient<{ url: string }>(
        "/api/stripe/checkout/sessions",
        {
          method: "POST",
          body: JSON.stringify({ priceId, kind: "subscription" }),
        },
      );

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error("Checkout error:", err);
      alert(getCheckoutErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="border rounded-lg p-6 flex flex-col">
      <h2 className="text-2xl font-semibold mb-2">{name}</h2>
      {trialDays && (
        <p className="text-sm text-muted-foreground mb-4">
          with {trialDays} day free trial
        </p>
      )}
      <p className="text-4xl font-medium mb-6">
        ${price / 100}{" "}
        <span className="text-base font-normal text-muted-foreground">/ {interval}</span>
      </p>
      <ul className="mb-6 space-y-2 flex-1">
        {features.map((feature) => (
          <li key={feature} className="text-sm text-muted-foreground">
            • {feature}
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={handleSubscribe}
        disabled={!available || submitting}
        className="w-full bg-primary text-primary-foreground rounded-md py-2 disabled:opacity-50"
      >
        {submitting ? "Processing..." : available ? "Subscribe" : "Unavailable"}
      </button>
    </div>
  );
}

function TopupCard({ price }: { price: Price }) {
  const [submitting, setSubmitting] = useState(false);

  async function handleTopupCheckout() {
    setSubmitting(true);

    try {
      const data = await apiClient<{ url: string }>(
        "/api/stripe/checkout/sessions",
        {
          method: "POST",
          body: JSON.stringify({ priceId: price.id, kind: "topup" }),
        },
      );

      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      console.error("Top-up checkout error:", err);
      alert(getCheckoutErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="border rounded-lg p-5 flex flex-col">
      <h3 className="text-lg font-semibold">{price.product.name}</h3>
      {price.product.description ? (
        <p className="text-sm text-muted-foreground mt-1">{price.product.description}</p>
      ) : null}
      <p className="text-2xl font-medium mt-4 mb-5">${(price.unit_amount ?? 0) / 100}</p>
      <button
        type="button"
        onClick={handleTopupCheckout}
        disabled={submitting}
        className="w-full bg-secondary text-secondary-foreground rounded-md py-2 disabled:opacity-50 mt-auto"
      >
        {submitting ? "Processing..." : "Buy Credits"}
      </button>
    </div>
  );
}
